import { Button, Flex, Modal, SearchField, Skeleton } from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import {
  Condition,
  ConditionByModal,
} from '@jane/business-admin/components/shared/specials/modal/conditions/generic_components/ConditionByModal';
import { LoadingCondition } from '@jane/business-admin/components/shared/specials/modal/conditions/generic_components/LoadingCondition';
import { ConditionType } from '@jane/business-admin/components/shared/specials/modal/form';
import { useBrandsForRulesets } from '@jane/business-admin/data-access';
import { useRetailOrgSettings, useStoreId } from '@jane/business-admin/hooks';
import type { Brand } from '@jane/business-admin/types';
import { ModalNames } from '@jane/business-admin/util';

import { BrandCheckboxRow } from './BrandCheckboxRow';
import type { BrandObj } from './toSelectedBrands';
import { toSelectedBrands } from './toSelectedBrands';

const BrandPickerLoading = () => {
  return (
    <>
      {Array.from({ length: 10 }, (_, i) => (
        <Skeleton
          animate
          height={72}
          style={{
            boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.1)',
          }}
          key={i}
        >
          <Flex py={12} px={24} height={72} alignItems="center">
            <Skeleton.Bone borderRadius="circular" width={46} height={46} />
            <Skeleton.Bone width="90%" ml={24} />
          </Flex>
        </Skeleton>
      ))}
    </>
  );
};

export const BrandConditions = ({
  disableAddButton,
  fieldPrefix,
  footer,
  isGlobalSpecial,
  type,
}: {
  disableAddButton?: boolean;
  fieldPrefix: string;
  footer?: ReactNode;
  isGlobalSpecial?: boolean;
  type: ConditionType.Brands | ConditionType.ExcludeBrands;
}) => {
  const storeId = useStoreId();
  const { orgId } = useRetailOrgSettings();

  const [query, setQuery] = useState('');
  const [brandConditionsModalOpen, setBrandConditionsModalOpen] =
    useState(false);

  const { getValues, setValue, watch } = useFormContext();

  const rulesWatch = watch(`${fieldPrefix}.rules`);
  const includesOrExcludes =
    type === ConditionType.Brands ? 'includes' : 'excludes';

  // Read once rather than watched: this is the revert target when the picker's
  // changes are discarded, so it has to stay put while the rules are edited.
  const [initialSelectedBrands] = useState<BrandObj[]>(() =>
    toSelectedBrands(
      getValues(`${fieldPrefix}.rules.${includesOrExcludes}.0.brand_ids`),
      getValues('rules_entities')?.brands
    )
  );

  const [selectedBrands, setSelectedBrands] = useState<BrandObj[]>(
    initialSelectedBrands
  );

  const wrapperRef = useRef(null);

  // brand data to populate the brand selection modal
  const {
    data: brandData,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useBrandsForRulesets({
    isGlobal: Boolean(isGlobalSpecial),
    names: query,
    orgId,
    storeId,
  });

  const { ref } = useInView({
    root: wrapperRef.current,
    threshold: 0,
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
      }
    },
    skip: isFetchingNextPage,
  });

  const rules =
    rulesWatch && rulesWatch[includesOrExcludes]
      ? rulesWatch[includesOrExcludes]
      : null;

  // when adding new brand, data is selectedBrands
  // when removing by clicking pill, data is the remaining Conditions
  // when removing all, data is []
  // selectedBrands will only change when removing one or all brands
  const onSubmit = (data: Condition[] | BrandObj[]) => {
    const isConditionArray = (
      value: Condition[] | BrandObj[]
    ): value is Condition[] => Condition.array().safeParse(value).success;

    // ensure all selected brands are represented with names & IDs in the conditions/rules we send to the server
    const idValues = isConditionArray(data)
      ? data.map(({ value }) => parseInt(value))
      : data.map(({ product_brand_id }) => product_brand_id);

    const newSelectedBrands = selectedBrands.filter((obj) =>
      idValues.includes(obj.product_brand_id)
    );

    const nameValues = newSelectedBrands.map((obj) => obj.name);

    const firstCondition = rules?.[0] ?? {};
    setValue(
      `${fieldPrefix}.rules`,
      {
        ...rulesWatch,
        [includesOrExcludes]: [
          { ...firstCondition, brands: nameValues, brand_ids: idValues },
        ],
      },
      { shouldDirty: true }
    );

    setSelectedBrands(newSelectedBrands);
    setBrandConditionsModalOpen(false);
    setQuery('');
  };

  const select = (brand: Brand) => {
    // We are de-selecting a brand, so we filter it out
    const newSelectedBrands = selectedBrands.filter(
      (brandObj) => brandObj.product_brand_id !== brand.productBrandId
    );

    // We are selecting a brand since our newly selected brand was not found in the filter operation and our lengths remain the same
    if (selectedBrands.length === newSelectedBrands.length) {
      newSelectedBrands.push({
        product_brand_id: brand.productBrandId,
        name: brand.name,
      });
    }

    setSelectedBrands(newSelectedBrands);
  };

  const isSelected = (brand: Brand) =>
    !!selectedBrands.find(
      (obj) => obj.product_brand_id === brand.productBrandId
    );

  const conditions = selectedBrands.map((brandObj) => ({
    value: brandObj.product_brand_id.toString(),
    label: brandObj.name,
  }));

  return (
    <>
      {!brandData ? (
        <LoadingCondition />
      ) : (
        <ConditionByModal
          disableAddButton={disableAddButton}
          footer={footer}
          onOpenModal={() => setBrandConditionsModalOpen(true)}
          type={type}
          conditions={conditions}
          conditionsLoading={isFetching}
          onConditionsUpdate={onSubmit}
        />
      )}
      <ConfirmWrapperWithTracking
        open={brandConditionsModalOpen}
        onCloseDiscardChanges={() => {
          setSelectedBrands(initialSelectedBrands);
        }}
        setOpen={() => {
          setQuery('');
          setBrandConditionsModalOpen(false);
        }}
        hasChanges={selectedBrands.length !== conditions?.length}
        modalName={ModalNames.BrandConditions}
        overlayClose={true}
      >
        <>
          <Modal.Header
            title="Select brands"
            actions={
              <Button
                variant="primary"
                label="Save"
                onClick={() => onSubmit(selectedBrands)}
                disabled={selectedBrands.length === 0}
              />
            }
          />
          <Modal.Content ref={wrapperRef}>
            <SearchField
              borderRadius="sm"
              label="Search Brands"
              name="brand-search-field"
              onChange={setQuery}
              placeholder="Search for a brand"
              mb={24}
            />
            {brandData?.map((brand, i) => {
              const isFirst = i === 0;
              const isLast = i === brandData.length - 1;
              const needsDivider = !isFirst;

              return (
                <BrandCheckboxRow
                  key={brand.id}
                  brand={brand}
                  onClick={() => select(brand)}
                  selected={isSelected(brand)}
                  divider={needsDivider}
                  ref={isLast ? ref : undefined}
                />
              );
            })}
            {isFetching && <BrandPickerLoading />}
          </Modal.Content>
        </>
      </ConfirmWrapperWithTracking>
    </>
  );
};
